import { XPEventType, getXPEventLabel, getXPEventIcon } from '../value-objects/XPEventType';

export interface XPTransactionProps {
  id: string;
  studentId: string;
  eventType: XPEventType;
  baseXP: number;
  multiplier: number;
  finalXP: number;
  relatedEntityId?: string;
  metadata?: {
    lessonId?: string;
    evaluationId?: string;
    videoUrl?: string;
    quizScore?: number;
    activityName?: string;
  };
  weeklyCycleId: string;
  createdAt: Date;
}

export class XPTransaction {
  private readonly props: XPTransactionProps;

  constructor(props: XPTransactionProps) {
    this.props = props;
  }

  get id(): string {
    return this.props.id;
  }

  get studentId(): string {
    return this.props.studentId;
  }

  get eventType(): XPEventType {
    return this.props.eventType;
  }

  get baseXP(): number {
    return this.props.baseXP;
  }

  get multiplier(): number {
    return this.props.multiplier;
  }

  get finalXP(): number {
    return this.props.finalXP;
  }

  get relatedEntityId(): string | undefined {
    return this.props.relatedEntityId;
  }

  get metadata(): XPTransactionProps['metadata'] {
    return this.props.metadata;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  /**
   * Check if this XP was earned during a boost
   */
  isBoosted(): boolean {
    return this.props.multiplier > 1.0;
  }

  /**
   * Get the label for this event type
   */
  getLabel(): string {
    return getXPEventLabel(this.props.eventType);
  }

  /**
   * Get the icon for this event type
   */
  getIcon(): string {
    return getXPEventIcon(this.props.eventType);
  }

  /**
   * Format XP with boost indicator
   */
  getDisplayText(): string {
    const boostSuffix = this.isBoosted() ? ' (2x)' : '';
    return `+${this.props.finalXP} XP${boostSuffix}`;
  }

  /**
   * Check if this is from a specific week
   */
  isFromWeek(weekStart: Date, weekEnd: Date): boolean {
    return this.props.createdAt >= weekStart && this.props.createdAt <= weekEnd;
  }

  /**
   * Get time since this transaction
   */
  getTimeSince(): string {
    const now = new Date();
    const diff = now.getTime() - this.props.createdAt.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes}m`;
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${days}d`;
  }

  toPlainObject(): XPTransactionProps {
    return { ...this.props };
  }
}
